const MLS_ENTRY_VIEWPORT_RATIO = 0.75;
const MLS_HEADER_DURATION = 0.6;
const MLS_TRAVEL_EPSILON = 1;

/**
 * Landscape spotlight. Native overflow + CSS snap paging for the card
 * track and the tablist. First item pins to 0, last page pins to
 * maxScroll, and previous always changes travel.
 *
 * Tabs, keyboard support, and track transitions work without GSAP.
 * GSAP is limited to the optional scroll-triggered entrance sequence.
 */
class MastonLandscapeSpotlight extends HTMLElement {
  #controller;
  #matchMedia;
  #resizeObserver;
  #indicatorFrame;
  #cardScrollFrame = 0;
  #tabScrollFrame = 0;
  #cardTweenFrame = 0;
  #tabTweenFrame = 0;
  #resizeFrame = 0;
  #activeIndex = 0;
  #cardIndex = 0;
  #tabIndex = 0;
  #isCardAnimating = false;
  #isTabAnimating = false;
  #tablist;
  #indicator;
  #viewport;
  #track;
  #header;
  #tabsRoot;
  #tabs = [];
  #panels = [];
  #slides = [];

  connectedCallback() {
    if (this.#controller) return;

    this.#viewport = this.querySelector('[data-mls-viewport]');
    this.#track = this.querySelector('[data-mls-track]');
    this.#tablist = this.querySelector('[data-mls-tablist]');
    this.#indicator = this.querySelector('[data-mls-indicator]');
    this.#header = this.querySelector('[data-mls-header]');
    this.#tabsRoot = this.querySelector('[data-mls-tabs]');
    this.#panels = [...this.querySelectorAll('[data-mls-panel]')];

    if (!this.#panels.length || !this.#viewport || !this.#track) return;

    this.#controller = new AbortController();
    this.#activeIndex = Math.max(
      0,
      this.#panels.findIndex((panel) => panel.hasAttribute('data-mls-active'))
    );
    this.#tabIndex = this.#activeIndex;

    this.#collectSlides();
    this.#buildTabsFromPanels();
    this.#commitPanel(this.#activeIndex);
    this.#ensureFocusedCards();
    this.#cardIndex = Math.min(this.#categoryStartIndex(this.#activeIndex), this.#cardMaxIndex());
    this.#bindEvents();
    this.classList.add('is-enhanced');
    this.#syncIndicator({ animate: false });
    requestAnimationFrame(() => {
      this.#scrollCardsToIndex(this.#cardIndex, 'auto');
      this.#scrollTabsToIndex(this.#activeIndex, 'auto');
    });

    this.#setupMotionWhenReady();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;

    cancelAnimationFrame(this.#indicatorFrame);
    cancelAnimationFrame(this.#cardScrollFrame);
    cancelAnimationFrame(this.#tabScrollFrame);
    cancelAnimationFrame(this.#resizeFrame);
    this.#cancelTween(this.#viewport, 'card');
    this.#cancelTween(this.#tablist, 'tab');

    this.#indicatorFrame = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#matchMedia?.revert();
    this.#matchMedia = undefined;
    this.classList.remove('is-enhanced', 'is-measured', 'is-motion', 'is-entered');
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Mobile pages a 2×2 category. Tablet and desktop page individual cards.
   * @returns {boolean}
   */
  #isPageMode() {
    return !window.matchMedia('(min-width: 768px)').matches;
  }

  #cardsIn(panel) {
    if (!panel) return [];
    return [...panel.querySelectorAll('[data-mls-card]')];
  }

  #collectSlides() {
    this.querySelectorAll('[data-mls-snap-end]').forEach((node) => {
      node.removeAttribute('data-mls-snap-end');
    });

    if (this.#isPageMode()) {
      this.#slides = this.#panels.slice();
    } else {
      this.#slides = [...this.querySelectorAll('[data-mls-card]')].map(
        (card) => card.closest('.shopify-block__maston-landscape-card') || card
      );
    }

    this.#slides.at(-1)?.setAttribute('data-mls-snap-end', '');
  }

  /**
   * Theme blocks render through `{% content_for 'blocks' %}` and often
   * leave `section.blocks` empty, so Liquid cannot print the tablist.
   * Build (or keep) tabs from the already-rendered panels.
   */
  #buildTabsFromPanels() {
    this.querySelectorAll('[data-mls-fallback-tab]').forEach((tab) => tab.remove());
    this.#tabs = this.#tablist ? [...this.#tablist.querySelectorAll('[data-mls-tab]')] : [];

    if (this.#panels.length < 2) {
      this.#tabsRoot?.setAttribute('hidden', '');
      this.#tabs = [];
      return;
    }

    if (this.#tabs.length === this.#panels.length) {
      this.#tabsRoot?.removeAttribute('hidden');
      return;
    }

    if (!this.#tablist) return;

    this.#tabs.forEach((tab) => tab.remove());

    this.#panels.forEach((panel, index) => {
      const isActive = index === this.#activeIndex;
      const tab = document.createElement('button');
      const tabId =
        panel.getAttribute('aria-labelledby') ||
        `mls-tab-${this.dataset.sectionId || 'section'}-${panel.dataset.mlsBlockId || index}`;
      const label = panel.dataset.mlsLabel?.trim() || '';

      tab.className = 'maston-landscape-spotlight__tab';
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.id = tabId;
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
      tab.setAttribute('data-mls-tab', '');
      tab.dataset.mlsIndex = String(index);
      if (panel.dataset.mlsBlockId) tab.dataset.mlsBlockId = panel.dataset.mlsBlockId;
      tab.dataset.mlsLabel = label;

      const labelEl = document.createElement('span');
      labelEl.className = 'maston-landscape-spotlight__tab-label';
      labelEl.textContent = label;
      tab.append(labelEl);
      const endSpacer = this.#tablist.querySelector(
        '.maston-landscape-spotlight__tab-spacer:not(.maston-landscape-spotlight__tab-spacer--start)'
      );
      this.#tablist.insertBefore(tab, endSpacer);
    });

    this.#tabs = [...this.#tablist.querySelectorAll('[data-mls-tab]')];
    this.#tabsRoot?.removeAttribute('hidden');
  }

  #ensureFocusedCards() {
    this.#panels.forEach((panel) => {
      const cards = this.#cardsIn(panel);
      if (!cards.length) return;
      if (cards.some((card) => card.hasAttribute('data-mls-focused'))) return;
      cards[0].setAttribute('data-mls-focused', '');
    });
  }

  #bindEvents() {
    const { signal } = this.#controller;

    if (this.#tablist) {
      this.#tablist.addEventListener('click', this.#onTablistClick, { signal });
      this.#tablist.addEventListener('keydown', this.#onTablistKeydown, { signal });
      this.#tablist.addEventListener('scroll', this.#onTabScroll, { signal, passive: true });

      this.#resizeObserver = new ResizeObserver(() => this.#scheduleIndicatorSync());
      this.#resizeObserver.observe(this.#tablist);
    }

    this.#viewport?.addEventListener('scroll', this.#onCardScroll, { signal, passive: true });
    this.addEventListener('click', this.#onCardClick, { signal });
    this.addEventListener('keydown', this.#onCardKeydown, { signal });
    this.addEventListener('keydown', this.#onTrackKeydown, { signal });
    window.addEventListener('resize', this.#onResize, { signal, passive: true });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    }
  }

  #gapFor(scroller) {
    if (scroller === this.#viewport) {
      return Number.parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    }

    return Number.parseFloat(getComputedStyle(scroller).columnGap) || 0;
  }

  /**
   * Complete slides in view. `floor(perView)` for peek tracks.
   * @param {HTMLElement} scroller
   * @param {Element[]} slides
   * @returns {number}
   */
  #visibleCount(scroller, slides) {
    if (!scroller || !slides.length) return 1;
    if (scroller === this.#viewport && this.#isPageMode()) return 1;

    const slide = slides[0];
    const width = slide.getBoundingClientRect().width;
    const gap = this.#gapFor(scroller);
    if (width <= 0) return 1;

    const styles = getComputedStyle(scroller);
    const padStart = Number.parseFloat(styles.scrollPaddingInlineStart) || 0;
    const padEnd = Number.parseFloat(styles.scrollPaddingInlineEnd) || 0;
    const available = Math.max(0, scroller.clientWidth - padStart - padEnd);

    return Math.max(1, Math.floor((available + gap) / (width + gap)));
  }

  #maxIndex(scroller, slides) {
    return Math.max(0, slides.length - this.#visibleCount(scroller, slides));
  }

  #cardMaxIndex() {
    return this.#maxIndex(this.#viewport, this.#slides);
  }

  #tabMaxIndex() {
    return Math.max(0, this.#tabs.length - 1);
  }

  #baseDuration() {
    if (window.matchMedia('(min-width: 1280px)').matches) return 450;
    if (window.matchMedia('(min-width: 768px)').matches) return 400;
    return 350;
  }

  /**
   * @param {Element} slide
   * @param {HTMLElement} scroller
   * @returns {number}
   */
  #slideOffset(slide, scroller) {
    const pad = Number.parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 0;
    return Math.max(
      0,
      scroller.scrollLeft +
        slide.getBoundingClientRect().left -
        scroller.getBoundingClientRect().left -
        pad
    );
  }

  /**
   * @param {HTMLElement} scroller
   * @param {Element[]} slides
   * @param {number} index
   * @returns {number}
   */
  #targetLeft(scroller, slides, index) {
    const slide = slides[index];
    if (!slide || !scroller) return 0;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    if (index <= 0) return 0;
    const maxIndex = scroller === this.#tablist ? this.#tabMaxIndex() : this.#maxIndex(scroller, slides);
    if (index >= maxIndex) return maxScroll;
    if (scroller === this.#tablist) {
      const scrollerRect = scroller.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const centeredLeft =
        scroller.scrollLeft +
        slideRect.left -
        scrollerRect.left -
        (scroller.clientWidth - slideRect.width) / 2;
      return Math.max(0, Math.min(maxScroll, centeredLeft));
    }
    return this.#slideOffset(slide, scroller);
  }

  /**
   * Previous skips indexes that still sit on the end rail.
   * @param {HTMLElement} scroller
   * @param {Element[]} slides
   * @param {number} current
   * @param {number} direction
   * @returns {number}
   */
  #resolveIndex(scroller, slides, current, direction) {
    const max = this.#maxIndex(scroller, slides);
    let next = Math.max(0, Math.min(max, current + direction));

    if (direction < 0 && next > 0) {
      const here = this.#targetLeft(scroller, slides, current);
      while (next > 0 && Math.abs(this.#targetLeft(scroller, slides, next) - here) <= MLS_TRAVEL_EPSILON) {
        next -= 1;
      }
    }

    return next;
  }

  /**
   * @param {'card' | 'tab'} kind
   */
  #cancelTween(scroller, kind) {
    if (kind === 'card') {
      cancelAnimationFrame(this.#cardTweenFrame);
      this.#cardTweenFrame = 0;
      this.#isCardAnimating = false;
    } else {
      cancelAnimationFrame(this.#tabTweenFrame);
      this.#tabTweenFrame = 0;
      this.#isTabAnimating = false;
    }

    scroller?.classList.remove('is-scrolling');
  }

  /**
   * @param {HTMLElement} scroller
   * @param {number} target
   * @param {number} duration
   * @param {'card' | 'tab'} kind
   */
  #animateTo(scroller, target, duration, kind) {
    this.#cancelTween(scroller, kind);

    const start = scroller.scrollLeft;
    const delta = target - start;
    if (Math.abs(delta) < 1) {
      scroller.scrollLeft = target;
      return;
    }

    if (kind === 'card') this.#isCardAnimating = true;
    else this.#isTabAnimating = true;
    scroller.classList.add('is-scrolling');

    const t0 = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - t0) / duration);
      const eased =
        progress < 0.5 ? 4 * progress * progress * progress : 1 - (-2 * progress + 2) ** 3 / 2;
      scroller.scrollLeft = start + delta * eased;

      if (progress < 1) {
        const frame = requestAnimationFrame(tick);
        if (kind === 'card') this.#cardTweenFrame = frame;
        else this.#tabTweenFrame = frame;
        return;
      }

      scroller.scrollLeft = target;
      this.#cancelTween(scroller, kind);
    };

    const frame = requestAnimationFrame(tick);
    if (kind === 'card') this.#cardTweenFrame = frame;
    else this.#tabTweenFrame = frame;
  }

  /**
   * @param {HTMLElement} scroller
   * @param {Element[]} slides
   * @param {number} index
   * @param {'smooth' | 'auto'} behavior
   * @param {'card' | 'tab'} kind
   */
  #scrollScrollerTo(scroller, slides, index, behavior, kind) {
    if (!scroller || !slides.length) return;

    const maxIndex = kind === 'tab' ? this.#tabMaxIndex() : this.#maxIndex(scroller, slides);
    const clamped = Math.max(0, Math.min(index, maxIndex));
    if (kind === 'card') this.#cardIndex = clamped;
    else this.#tabIndex = clamped;

    const target = this.#targetLeft(scroller, slides, clamped);
    const distance = Math.abs(target - scroller.scrollLeft);
    const duration = Math.min(720, Math.max(this.#baseDuration(), distance * 0.55));

    if (behavior === 'auto' || this.#prefersReducedMotion) {
      this.#cancelTween(scroller, kind);
      scroller.scrollLeft = target;
      return;
    }

    this.#animateTo(scroller, target, duration, kind);
  }

  #scrollCardsToIndex(index, behavior = 'smooth') {
    this.#scrollScrollerTo(this.#viewport, this.#slides, index, behavior, 'card');
  }

  #syncPadCardGroupAlignment(panelIndex) {
    if (!this.#viewport) return;

    this.#viewport.style.removeProperty('scroll-padding-inline-start');
    if (!window.matchMedia('(min-width: 768px) and (max-width: 1279px)').matches || panelIndex !== 1) {
      return;
    }

    const cards = this.#cardsIn(this.#panels[panelIndex]);
    const firstCard = cards[0];
    const lastCard = cards.at(-1);
    if (!firstCard || !lastCard) return;

    const firstRect = firstCard.getBoundingClientRect();
    const lastRect = lastCard.getBoundingClientRect();
    const groupWidth = lastRect.right - firstRect.left;
    const gutter = Number.parseFloat(getComputedStyle(this).getPropertyValue('--mls-gutter')) || 0;
    const centeredInset = Math.max(gutter, (this.#viewport.clientWidth - groupWidth) / 2);
    this.#viewport.style.scrollPaddingInlineStart = `${centeredInset}px`;
  }

  #scrollTabsToIndex(index, behavior = 'smooth') {
    if (!this.#tablist || !this.#tabs.length) return;
    this.#scrollScrollerTo(this.#tablist, this.#tabs, index, behavior, 'tab');
  }

  #categoryStartIndex(panelIndex) {
    if (this.#isPageMode()) return panelIndex;

    let start = 0;
    for (let index = 0; index < panelIndex; index += 1) {
      start += this.#cardsIn(this.#panels[index]).length;
    }
    return start;
  }

  #categoryIndexFromSlide(slideIndex) {
    if (this.#isPageMode()) {
      return Math.max(0, Math.min(slideIndex, this.#panels.length - 1));
    }

    let remaining = slideIndex;
    for (let index = 0; index < this.#panels.length; index += 1) {
      const count = this.#cardsIn(this.#panels[index]).length;
      if (remaining < count) return index;
      remaining -= count;
    }

    return Math.max(0, this.#panels.length - 1);
  }

  #slideIndexFor(card) {
    const slide = card.closest('.shopify-block__maston-landscape-card') || card;
    if (this.#isPageMode()) {
      const panel = card.closest('[data-mls-panel]');
      return Math.max(0, this.#panels.indexOf(panel));
    }

    return Math.max(0, this.#slides.indexOf(slide));
  }

  #nearestIndex(scroller, slides) {
    const left = scroller.scrollLeft;
    let closest = 0;
    let best = Infinity;

    slides.forEach((slide, index) => {
      const distance = Math.abs(this.#slideOffset(slide, scroller) - left);
      if (distance < best) {
        best = distance;
        closest = index;
      }
    });

    const maxIndex = scroller === this.#tablist ? this.#tabMaxIndex() : this.#maxIndex(scroller, slides);
    return Math.min(closest, maxIndex);
  }

  #onCardScroll = () => {
    if (this.#isCardAnimating) return;

    cancelAnimationFrame(this.#cardScrollFrame);
    this.#cardScrollFrame = requestAnimationFrame(() => {
      this.#cardIndex = this.#nearestIndex(this.#viewport, this.#slides);
      const categoryIndex = this.#categoryIndexFromSlide(this.#cardIndex);
      if (categoryIndex !== this.#activeIndex) this.#activate(categoryIndex, { fromScroll: true });
    });
  };

  #onTabScroll = () => {
    if (this.#isTabAnimating) return;

    cancelAnimationFrame(this.#tabScrollFrame);
    this.#tabScrollFrame = requestAnimationFrame(() => {
      this.#tabIndex = this.#nearestIndex(this.#tablist, this.#tabs);
    });
  };

  #onResize = () => {
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(() => {
      this.#collectSlides();
      this.#cardIndex = Math.min(this.#cardIndex, this.#cardMaxIndex());
      this.#syncPadCardGroupAlignment(this.#activeIndex);
      this.#scrollCardsToIndex(this.#cardIndex, 'auto');
      this.#scrollTabsToIndex(Math.min(this.#activeIndex, this.#tabMaxIndex()), 'auto');
      this.#syncIndicator({ animate: false });
    });
  };

  #scheduleIndicatorSync() {
    if (this.#indicatorFrame) return;

    this.#indicatorFrame = requestAnimationFrame(() => {
      this.#indicatorFrame = undefined;
      this.#syncIndicator({ animate: false });
    });
  }

  /**
   * `offsetLeft` / `offsetWidth` are measured from the tablist padding edge,
   * matching the absolutely positioned underline.
   */
  #syncIndicator({ animate = true } = {}) {
    const activeTab = this.#tabs[this.#activeIndex];
    if (!this.#indicator || !activeTab) return;

    if (!animate) this.classList.remove('is-measured');

    this.#indicator.style.transform = `translate3d(${activeTab.offsetLeft}px, 0, 0)`;
    this.#indicator.style.inlineSize = `${activeTab.offsetWidth}px`;

    if (!animate) {
      requestAnimationFrame(() => this.classList.add('is-measured'));
    }
  }

  #onTablistClick = (event) => {
    const tab = event.target.closest('[data-mls-tab]');
    if (!tab || !this.#tablist.contains(tab)) return;

    this.#activate(Number(tab.dataset.mlsIndex));
  };

  #onTablistKeydown = (event) => {
    const tab = event.target.closest('[data-mls-tab]');
    if (!tab || !this.#tablist.contains(tab)) return;

    const lastIndex = this.#tabs.length - 1;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = isRtl ? this.#activeIndex - 1 : this.#activeIndex + 1;
        break;
      case 'ArrowLeft':
        nextIndex = isRtl ? this.#activeIndex + 1 : this.#activeIndex - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    if (nextIndex < 0) nextIndex = lastIndex;
    if (nextIndex > lastIndex) nextIndex = 0;

    event.preventDefault();
    this.#activate(nextIndex, { focusTab: true });
  };

  #onCardClick = (event) => {
    const card = event.target.closest('[data-mls-card]');
    if (!card || !this.contains(card)) return;

    const panel = card.closest('[data-mls-panel]');
    const panelIndex = this.#panels.indexOf(panel);

    if (panelIndex >= 0 && panelIndex !== this.#activeIndex) {
      this.#activate(panelIndex, { slideIndex: this.#slideIndexFor(card) });
    }

    this.#focusCard(card);
  };

  #onCardKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const card = event.target.closest('[data-mls-card]');
    if (!card || !this.contains(card)) return;

    this.#focusCard(card);
  };

  #onTrackKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (this.#tablist?.contains(event.target)) return;

    const card = event.target.closest?.('[data-mls-card]');
    if (!card || !this.contains(card)) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const direction = (event.key === 'ArrowRight') !== isRtl ? 1 : -1;

    event.preventDefault();
    this.#scrollCardsToIndex(
      this.#resolveIndex(this.#viewport, this.#slides, this.#cardIndex, direction)
    );
  };

  #onBlockSelect = (event) => {
    const panel = this.#panels.find(
      (item) => item === event.target || item.contains(event.target) || event.target.contains?.(item)
    );
    if (!panel) return;

    const index = this.#panels.indexOf(panel);
    if (index < 0) return;

    const card = event.target.closest?.('[data-mls-card]');
    const slideIndex = card && panel.contains(card) ? this.#slideIndexFor(card) : this.#categoryStartIndex(index);

    this.#activate(index, { slideIndex });
    if (card && panel.contains(card)) this.#focusCard(card);
  };

  /**
   * @param {Element} card
   */
  #focusCard(card) {
    if (!card) return;

    const panel = card.closest('[data-mls-panel]');
    this.#cardsIn(panel).forEach((item) => {
      item.toggleAttribute('data-mls-focused', item === card);
    });
  }

  #focusFirstCard(panel) {
    const cards = this.#cardsIn(panel);
    if (!cards.length) return;
    this.#focusCard(cards[0]);
  }

  #commitPanel(index) {
    this.#panels.forEach((panel, panelIndex) => {
      panel.toggleAttribute('data-mls-active', panelIndex === index);
    });
  }

  /**
   * @param {number} index
   * @param {{ focusTab?: boolean, fromScroll?: boolean, slideIndex?: number }} [options]
   */
  #activate(index, { focusTab = false, fromScroll = false, slideIndex } = {}) {
    if (!Number.isInteger(index)) return;
    if (index < 0 || index > this.#panels.length - 1) return;

    const previousIndex = this.#activeIndex;
    const activeTab = this.#tabs[index];

    if (focusTab) activeTab?.focus();
    if (index === previousIndex && fromScroll) return;

    if (index !== previousIndex) {
      this.#activeIndex = index;
      this.#tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });
      this.#syncIndicator();
    }

    const targetSlide =
      Number.isInteger(slideIndex) && slideIndex >= 0
        ? slideIndex
        : this.#categoryStartIndex(index);

    if (fromScroll) {
      this.#commitPanel(index);
      if (!Number.isInteger(slideIndex)) this.#focusFirstCard(this.#panels[index]);
      this.#scrollTabsToIndex(Math.min(index, this.#tabMaxIndex()));
      return;
    }

    this.#commitPanel(index);
    if (!Number.isInteger(slideIndex)) this.#focusFirstCard(this.#panels[index]);
    this.#syncPadCardGroupAlignment(index);
    this.#scrollCardsToIndex(Math.min(targetSlide, this.#cardMaxIndex()));
    this.#scrollTabsToIndex(Math.min(index, this.#tabMaxIndex()));
  }

  #setupMotionWhenReady() {
    if (this.#trySetupMotion()) return;

    if (document.readyState === 'complete') {
      this.classList.add('is-entered');
      return;
    }

    window.addEventListener('load', this.#onWindowLoad, {
      once: true,
      signal: this.#controller.signal
    });
  }

  #onWindowLoad = () => {
    if (!this.#trySetupMotion()) this.classList.add('is-entered');
  };

  #trySetupMotion = () => {
    if (!this.#controller || this.#controller.signal.aborted) return false;

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger || gsap?.core?.globals?.().ScrollTrigger;

    if (gsap && ScrollTrigger) {
      this.#setupMotion(gsap, ScrollTrigger);
      return true;
    }

    return false;
  };

  #setupMotion(gsap, ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);

    this.classList.add('is-motion');

    if (!this.hasAttribute('data-mls-entrance') || this.#prefersReducedMotion) {
      this.classList.add('is-entered');
      return;
    }

    const isAlreadyInView =
      this.getBoundingClientRect().top < window.innerHeight * MLS_ENTRY_VIEWPORT_RATIO;

    this.#matchMedia = gsap.matchMedia();
    this.#matchMedia.add(
      { reduceMotion: '(prefers-reduced-motion: reduce)' },
      (context) => {
        if (context.conditions.reduceMotion || isAlreadyInView) {
          this.classList.add('is-entered');
          return undefined;
        }

        const visibleSlides = this.#slides.slice(
          0,
          this.#visibleCount(this.#viewport, this.#slides) + 1
        );
        const triggers = [];

        if (this.#header) {
          const headerTween = gsap.from(this.#header, {
            autoAlpha: 0,
            y: 24,
            duration: MLS_HEADER_DURATION,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: this,
              start: 'top 75%',
              once: true
            }
          });
          triggers.push(headerTween.scrollTrigger);
        }

        if (visibleSlides.length) {
          const cardsTween = gsap.fromTo(
            visibleSlides,
            { autoAlpha: 0, y: 12, filter: 'blur(8px)' },
            {
              autoAlpha: 1,
              y: 0,
              filter: 'blur(0px)',
              duration: MLS_HEADER_DURATION,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: this,
                start: 'top 65%',
                once: true
              },
              onComplete: () => {
                gsap.set(visibleSlides, { clearProps: 'filter,opacity,visibility,transform,y' });
              }
            }
          );
          triggers.push(cardsTween.scrollTrigger);
        }

        if (this.#tabsRoot && !this.#tabsRoot.hasAttribute('hidden') && this.#tabs.length) {
          const tabsTween = gsap.fromTo(
            this.#tabsRoot,
            { autoAlpha: 0, y: 12 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.45,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: this,
                start: 'top 70%',
                once: true,
                onEnter: () => this.#syncIndicator({ animate: true })
              },
              onComplete: () => this.classList.add('is-entered')
            }
          );
          triggers.push(tabsTween.scrollTrigger);
        } else {
          this.classList.add('is-entered');
        }

        return () => {
          triggers.forEach((trigger) => trigger?.kill());
        };
      },
      this
    );
  }
}

if (!customElements.get('maston-landscape-spotlight')) {
  customElements.define('maston-landscape-spotlight', MastonLandscapeSpotlight);
}
